/**
 * DXF 레이어 선택 모달: 표준 타입 옵션 및 convert 요청용 페이로드 빌더.
 * (백엔드 process_dxf layer_mapping 키 = DXF 레이어명, 값 = 표준 타입 문자열)
 */

export interface LayerLike {
  name: string
  suggestedType: string
}

/** 드롭다운·일괄 적용에 쓰는 고정 표준 타입 목록.
 *  layer_type_rules.py 의 suggested_type 값과 동기화 유지 — 분석에서 추천한 타입이
 *  여기 없으면 모달에서 auto-select 안 되고 fallback 으로 'p-parking-basic' 표시됨. */
export const DXF_STANDARD_TYPE_OPTIONS: ReadonlyArray<{ value: string; labelKo: string }> = [
  { value: 'p-parking-basic', labelKo: '일반 주차' },
  { value: 'p-parking-large', labelKo: '확장 주차' },
  { value: 'p-parking-small', labelKo: '경차 주차' },
  { value: 'p-parking-disable', labelKo: '장애인 주차' },
  { value: 'p-parking-electric', labelKo: '전기차 주차' },
  { value: 'p-parking-women', labelKo: '여성 주차' },
  { value: 'p-parking-delivery', labelKo: '택배 주차' },
  { value: 's-pillar', labelKo: '기둥' },
  { value: 's-outline', labelKo: '외곽선' },
  { value: 's-circulation-ramp', labelKo: '램프' },
  { value: 's-circulation-path', labelKo: '통로' },
  { value: 'p-light-cctv', labelKo: '조명 CCTV' },
  { value: 'p-warning-cctv', labelKo: '단방향 CCTV' },
  { value: 'l-light', labelKo: '조명' },
  { value: 'p-emergency', labelKo: '비상벨' },
  { value: 'p-sensor', labelKo: '센서' },
  { value: 'p-speaker', labelKo: '스피커' },
]

const TYPE_LABEL_FALLBACK: Record<string, string> = Object.fromEntries(
  DXF_STANDARD_TYPE_OPTIONS.map(o => [o.value, o.labelKo])
)

/**
 * getTypeDisplayName returns a short Korean label for a standard type string.
 */
export function getTypeDisplayName(type: string): string {
  if (TYPE_LABEL_FALLBACK[type]) {
    return TYPE_LABEL_FALLBACK[type]
  }
  if (type.startsWith('l-')) {
    return TYPE_LABEL_FALLBACK[type] || '조명'
  }
  return type
}

/**
 * effectiveSuggestedType resolves the type sent to convert: user override wins, else analysis.
 */
export function effectiveSuggestedType(
  layerName: string,
  layers: readonly LayerLike[],
  overrides: Readonly<Record<string, string>>
): string {
  if (Object.prototype.hasOwnProperty.call(overrides, layerName)) {
    const overridden = overrides[layerName]
    return TYPE_LABEL_FALLBACK[overridden] ? overridden : 'p-parking-basic'
  }
  const layer = layers.find(l => l.name === layerName)
  const analyzed = layer?.suggestedType || ''
  return TYPE_LABEL_FALLBACK[analyzed] ? analyzed : 'p-parking-basic'
}

export interface SelectedLayerPayload {
  name: string
  suggestedType: string
}

/**
 * buildConvertSelectedLayers builds POST /dxf-parser/convert selectedLayers from selection + overrides.
 */
export function buildConvertSelectedLayers(
  selectedLayerNames: readonly string[],
  layers: readonly LayerLike[],
  overrides: Readonly<Record<string, string>>
): SelectedLayerPayload[] {
  return selectedLayerNames.map(name => ({
    name,
    suggestedType: effectiveSuggestedType(name, layers, overrides)
  }))
}

/**
 * applyBulkTypeToSelection sets the same suggested type for every selected layer name in overrides.
 */
export function applyBulkTypeToSelection(
  prev: Record<string, string>,
  selectedNames: ReadonlySet<string>,
  typeValue: string
): Record<string, string> {
  const next = { ...prev }
  selectedNames.forEach(name => {
    next[name] = typeValue
  })
  return next
}
